/**
 * Human-readable error messages for AsicExecutor custom errors.
 * These are decoded from revert data by wagmi/viem when the ABI is
 * provided — this map turns the error names into user-facing copy.
 */

const ERROR_MESSAGES: Record<string, string> = {
  DeadlinePassed: "Transaction deadline has passed. Try again.",
  EmptyRoute: "No valid route found for this swap.",
  InvalidV4TokenOut: "Route configuration error (V4 token mismatch).",
  MinOutNotMet: "Price moved unfavorably — you would receive less than your minimum. Try increasing slippage.",
  PoolOverpay: "Route calculation error (pool overpay). Please try again.",
  Reentrant: "Transaction was blocked for safety (reentrancy).",
  SameTokenInOut: "Input and output tokens must be different.",
  UnauthorizedCallback: "Unauthorized callback — please report this issue.",
  UnknownPoolKind: "Unknown pool type in route.",
  V3SwapDeltaInvalid: "Uniswap V3 swap produced unexpected amounts.",
};

/**
 * Extract a clean, user-facing message from a transaction error.
 * Handles wagmi/viem contract revert errors, user rejections, and
 * generic RPC errors.
 */
export function friendlyError(err: unknown): string {
  const msg = (err as Error)?.message ?? String(err);

  // User rejected in wallet
  if (msg.includes("User rejected") || msg.includes("user rejected") || msg.includes("ACTION_REJECTED")) {
    return "Transaction rejected in wallet.";
  }

  // Viem contract revert — errorName is set when ABI is provided
  const errorName = (err as { errorName?: string })?.errorName;
  if (errorName && ERROR_MESSAGES[errorName]) {
    return ERROR_MESSAGES[errorName];
  }

  // Check for known error names in the message string
  for (const [name, friendly] of Object.entries(ERROR_MESSAGES)) {
    if (msg.includes(name)) return friendly;
  }

  // Insufficient funds
  if (msg.includes("insufficient funds") || msg.includes("exceeds balance")) {
    return "Insufficient funds for gas.";
  }

  // Generic — truncate long RPC blobs
  if (msg.length > 200) {
    return msg.slice(0, 180) + "…";
  }

  return msg;
}
